import React, { useRef, useEffect, useState, useCallback } from 'react';
import API from '../services/api';

const POLL_INTERVAL = 3000;
const CANVAS_W = 1100;
const CANVAS_H = 420;
const MIN_SCALE = 0.5;
const MAX_SCALE = 4;

const FRAME_MAT    = 12;  // cream mat ring, px
const FRAME_WOOD   = 10;  // walnut wood ring, px
const FRAME_GUTTER = 38;  // layout clearance: 24px frame + 14px shadow room

const BRUSH_SIZES = [
  { label: 'S', value: 2, dot: 7 },
  { label: 'M', value: 4, dot: 11 },
  { label: 'L', value: 8, dot: 17 },
];

const TEXT_SIZES = [
  { label: 'S', value: 18 },
  { label: 'M', value: 28 },
  { label: 'L', value: 44 },
];

function fmtWeek(weekStart) {
  if (!weekStart) return '';
  const dateStr = (typeof weekStart === 'string' ? weekStart : weekStart.toISOString()).split('T')[0];
  const d = new Date(dateStr + 'T00:00:00');
  const end = new Date(d);
  end.setDate(d.getDate() + 6);
  const opts = { month: 'short', day: 'numeric' };
  return `${d.toLocaleDateString('en-US', opts)} – ${end.toLocaleDateString('en-US', { ...opts, year: 'numeric' })}`;
}

// ── TextPreview: draggable, rotatable overlay rendered during text placement ──
function TextPreview({ draft, onDragStart, onRotateStart }) {
  return (
    <div
      style={{
        position: 'absolute',
        left: draft.x * CANVAS_W,
        top: draft.y * CANVAS_H,
        transform: `translate(-50%, -50%) rotate(${draft.rotation}deg)`,
        cursor: 'move',
        userSelect: 'none',
        pointerEvents: 'all',
        zIndex: 10,
      }}
      onMouseDown={onDragStart}
      onTouchStart={onDragStart}
    >
      <span style={{
        fontFamily: 'Arial, Helvetica, sans-serif',
        fontSize: draft.fontSize,
        fontWeight: 700,
        color: draft.color,
        display: 'block',
        whiteSpace: 'pre',
        lineHeight: 1,
        pointerEvents: 'none',
        minWidth: 24,
        textShadow: '0px 1px 4px rgba(0,0,0,0.35)',
        outline: '1.5px dashed rgba(120,120,120,0.4)',
        outlineOffset: 4,
      }}>
        {draft.text || '   '}
      </span>
      {/* Rotation handle */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          right: -26,
          transform: 'translateY(-50%)',
          width: 14, height: 14,
          borderRadius: '50%',
          background: '#fff',
          border: '2px solid #9CA3AF',
          cursor: 'grab',
          boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
          pointerEvents: 'all',
          zIndex: 11,
        }}
        onMouseDown={e => { e.stopPropagation(); onRotateStart(e); }}
        onTouchStart={e => { e.stopPropagation(); onRotateStart(e); }}
      />
    </div>
  );
}

const CRAYON_PALETTE = [
  '#CC0000','#FF3333','#FF6666','#FF9999',
  '#FF007F','#FF69B4','#FF1493','#FFB6C1',
  '#FF6600','#FF8C00','#FFA040','#FFB347',
  '#FFD700','#FFE066','#FFFF00','#FFFACD',
  '#9ACD32','#ADFF2F','#7FFF00','#32CD32',
  '#008000','#228B22','#00CC00','#00FA9A',
  '#20B2AA','#008B8B','#00CED1','#00FFFF',
  '#87CEEB','#00BFFF','#1E90FF','#4169E1',
  '#0000FF','#0000CD','#000080','#191970',
  '#8A2BE2','#9400D3','#9370DB','#8B008B',
  '#EE82EE','#DDA0DD','#DA70D6','#FF00FF',
  '#8B4513','#D2691E','#CD853F','#F4A460',
  '#000000','#555555','#AAAAAA','#FFFFFF',
];

function StoryboardPanel({ strokes, textItems }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#fffef7';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const scaleX = canvas.width / 1100;
    const scaleY = canvas.height / 420;

    strokes.forEach(stroke => {
      const pts = typeof stroke.points === 'string' ? JSON.parse(stroke.points) : stroke.points;
      if (!pts || pts.length < 2) return;
      ctx.beginPath();
      ctx.strokeStyle = stroke.color;
      ctx.lineWidth = Math.max(1, stroke.stroke_width * Math.min(scaleX, scaleY));
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.moveTo(pts[0].x * canvas.width, pts[0].y * canvas.height);
      for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x * canvas.width, pts[i].y * canvas.height);
      ctx.stroke();
    });

    textItems.forEach(item => {
      ctx.font = `bold ${Math.max(8, item.font_size * Math.min(scaleX, scaleY))}px Arial`;
      ctx.fillStyle = item.color;
      ctx.save();
      ctx.translate(item.x * canvas.width, item.y * canvas.height);
      ctx.rotate((item.rotation || 0) * Math.PI / 180);
      ctx.fillText(item.text_content, 0, 0);
      ctx.restore();
    });
  }, [strokes, textItems]);

  return <canvas ref={canvasRef} width={440} height={168} style={{ width:'100%', display:'block', background:'#fffef7' }} />;
}

const MODE_RULES = {
  battle_royale: {
    emoji: '⚔️',
    title: 'Battle Royale Week!',
    rules: [
      'Claim any 2 colors from the full palette — first come, first served.',
      'Once a color is claimed, nobody else can use it this week.',
      'The grab popup appears when you open the canvas. Pick fast!',
      'Your 2 colors are yours for the entire week.',
    ],
  },
  anarchy: {
    emoji: '🔥',
    title: 'Anarchy Week!',
    rules: [
      'The full color palette is available to everyone — change color any time.',
      "You can erase anyone's strokes, and they can erase yours.",
      'No rules. Just create.',
    ],
  },
  storyboard: {
    emoji: '📖',
    title: 'Storyboard Week!',
    rules: [
      'A new chapter opens each day Monday through Thursday.',
      "Each day's canvas locks at midnight — add to that day's panel while it's open.",
      'Thursday night the full 4-panel story is revealed.',
      'Your colors stay the same across all four chapters.',
    ],
  },
};

export default function CommunityCanvas() {
  // ── Canvas + stroke refs ────────────────────────────────────────────────
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const strokesRef = useRef([]);
  const textItemsRef = useRef([]);
  const activeStrokeRef = useRef([]);
  const lastStrokeIdRef = useRef(0);
  const lastTextIdRef = useRef(0);
  const isDrawingRef = useRef(false);
  const pollTimerRef = useRef(null);
  const mySessionStrokesRef = useRef([]);
  const mySessionTextRef = useRef([]);

  // Text drag / rotate refs (used inside stable event-listener closures)
  const isDraggingTextRef = useRef(false);
  const isRotatingTextRef = useRef(false);
  const dragStartRef = useRef(null);   // { mx, my, ox, oy }
  const rotateStartRef = useRef(null); // { cx, cy, startAngle, origRotation }
  const draftTextRef = useRef(null);   // always-current mirror of draftText state
  const cardRef = useRef(null);
  const transformRef = useRef({ scale: 1, tx: 0, ty: 0 });
  const gestureRef = useRef(null);

  // ── Canvas live state ───────────────────────────────────────────────────
  const [userColors, setUserColors] = useState({ color1: '#888', color2: '#aaa' });
  const [selectedColor, setSelectedColor] = useState(null);
  const [brushSize, setBrushSize] = useState(4);
  const [weekStart, setWeekStart] = useState('');
  const [prompt, setPrompt] = useState(null);
  const [activeUsers, setActiveUsers] = useState([]);
  const [poolCount, setPoolCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [canvasReady, setCanvasReady] = useState(false);

  // ── Mobile state ────────────────────────────────────────────────────────
  const [isMobile, setIsMobile] = useState(false);
  const [containerWidth, setContainerWidth] = useState(CANVAS_W);
  const [mobileDrawMode, setMobileDrawMode] = useState(false);

  // ── View state ──────────────────────────────────────────────────────────
  const [view, setView] = useState('live');
  const [archives, setArchives] = useState([]);
  const [archivesLoading, setArchivesLoading] = useState(false);
  const [archiveDetail, setArchiveDetail] = useState(null);
  const [archiveDetailLoading, setArchiveDetailLoading] = useState(false);

  // ── Undo / redo ─────────────────────────────────────────────────────────
  const [undoStack, setUndoStack] = useState([]);
  const [myActionCount, setMyActionCount] = useState(0);

  // ── Text mode ───────────────────────────────────────────────────────────
  const [isTextMode, setIsTextMode] = useState(false);
  const [draftText, setDraftText] = useState(null); // { x, y, text, color, fontSize, rotation }
  const [textEditActive, setTextEditActive] = useState(false); // gate for global drag listeners

  // ── Prompt suggestion ───────────────────────────────────────────────────
  const [promptInput, setPromptInput] = useState('');
  const [promptStatus, setPromptStatus] = useState(null);

  // ── Canvas mode ──────────────────────────────────────────────────────────
  const [fullPalette,  setFullPalette]  = useState(false);
  const [showVipPicker, setShowVipPicker] = useState(false);
  const [mode,         setMode]         = useState('normal');
  const [nextWeekMode, setNextWeekMode] = useState('normal');
  const [showSplash,   setShowSplash]   = useState(false);
  const [showRules,    setShowRules]    = useState(false);
  const [countdown,    setCountdown]    = useState('');
  const [brCountdown,  setBrCountdown]  = useState('');
  const [claimedColors, setClaimedColors] = useState(null);
  const [allClaimedColors, setAllClaimedColors] = useState([]);
  const [grabPick,         setGrabPick]         = useState([]);
  const [grabLoading,      setGrabLoading]      = useState(false);
  const [grabError,        setGrabError]        = useState('');

  // ── Anarchy mode state ───────────────────────────────────────────────────
  const [anarchyColor,     setAnarchyColor]     = useState(CRAYON_PALETTE[0]);
  const [anarchyEraseMode, setAnarchyEraseMode] = useState(false);

  // ── Eraser tool ──────────────────────────────────────────────────────────
  const [eraseMode, setEraseMode] = useState(false);
  const myUserIdRef    = useRef(null);
  const erasingIdsRef  = useRef(new Set());

  // ── Storyboard mode state ─────────────────────────────────────────────────
  const [storyboard,  setStoryboard]  = useState(null);
  const [activeDay,   setActiveDay]   = useState(null);
  const [dailyPrompt, setDailyPrompt] = useState(null);

  // ── Storyboard suggestion modal ─────────────────────────────────────────────
  const [showSuggest,   setShowSuggest]   = useState(false);
  const [suggestDay,    setSuggestDay]    = useState(0);
  const [suggestText,   setSuggestText]   = useState('');
  const [suggestMsg,    setSuggestMsg]    = useState('');
  const [nextWeekStart, setNextWeekStart] = useState('');

  // Keep draftTextRef in sync for use inside stable closures
  useEffect(() => { draftTextRef.current = draftText; }, [draftText]);

  useEffect(() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      if (u.id) myUserIdRef.current = u.id;
    } catch {}
  }, []);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      if (!entries.length) return;
      const w = entries[0].contentRect.width;
      setContainerWidth(w);
      setIsMobile(w < 900);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (mobileDrawMode) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileDrawMode]);

  // ── Draw helpers ─────────────────────────────────────────────────────────
  const drawStroke = useCallback((ctx, stroke, w, h) => {
    const pts = stroke.points;
    if (!pts || pts.length < 2) return;
    ctx.save();
    ctx.strokeStyle = stroke.color;
    ctx.lineWidth = stroke.stroke_width || 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(pts[0].x * w, pts[0].y * h);
    for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x * w, pts[i].y * h);
    ctx.stroke();
    ctx.restore();
  }, []);

  const drawTextItem = useCallback((ctx, item, w, h) => {
    if (!item.text_content) return;
    // Scale font proportionally to canvas height so it always matches the HTML
    // preview regardless of actual canvas buffer dimensions.
    const px = Math.round(((item.font_size || 28) / CANVAS_H) * h);
    ctx.save();
    ctx.translate(item.x * w, item.y * h);
    ctx.rotate(((item.rotation || 0) * Math.PI) / 180);
    ctx.font = `bold ${px}px Arial, Helvetica, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowColor = 'rgba(0,0,0,0.35)';
    ctx.shadowBlur = 4;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 1;
    ctx.fillStyle = item.color;
    ctx.fillText(item.text_content, 0, 0);
    ctx.restore();
  }, []);

  // Strokes rendered first; text overlays on top (like a caption layer)
  const redrawCanvas = useCallback((strokes, texts) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    (strokes || []).forEach(s => drawStroke(ctx, s, canvas.width, canvas.height));
    (texts || []).forEach(t => drawTextItem(ctx, t, canvas.width, canvas.height));
  }, [drawStroke, drawTextItem]);

  const redrawAll = useCallback(
    () => redrawCanvas(strokesRef.current, textItemsRef.current),
    [redrawCanvas]
  );

  // ── Initial canvas load ───────────────────────────────────────────────────
  useEffect(() => {
    let mounted = true;
    API.get('/drawing/canvas').then(res => {
      if (!mounted) return;
      const { strokes, textItems, userColors: uc, weekStart: ws, activeUsers: au, prompt: p, poolCount: pc } = res.data;
      setUserColors(uc);
      setSelectedColor(uc.color1);
      setWeekStart(ws);
      setActiveUsers(au || []);
      setPrompt(p || null);
      setPoolCount(pc || 0);
      strokesRef.current = strokes || [];
      textItemsRef.current = textItems || [];
      if (strokes?.length) lastStrokeIdRef.current = strokes[strokes.length - 1].id;
      if (textItems?.length) lastTextIdRef.current = textItems[textItems.length - 1].id;
      setFullPalette(res.data.fullPalette || false);
      setMode(res.data.mode || 'normal');
      setAnarchyEraseMode(false);
      setNextWeekMode(res.data.nextWeekMode || 'normal');
      setNextWeekStart(res.data.nextWeekStart || '');
      setClaimedColors(res.data.claimedColors || null);
      setAllClaimedColors(res.data.allClaimedColors || []);
      // Battle Royale: draw with the colors you claimed, not the weekly assigned pair.
      if (res.data.mode === 'battle_royale' && res.data.claimedColors) {
        setSelectedColor(res.data.claimedColors.color1);
      }
      setStoryboard(res.data.storyboard || null);
      setActiveDay(res.data.activeDay ?? null);
      setDailyPrompt(res.data.dailyPrompt || null);
      if (res.data.mode && res.data.mode !== 'normal') {
        const key = `canvas_mode_seen_${res.data.weekStart}`;
        if (!localStorage.getItem(key)) {
          setShowSplash(true);
        }
      }
      setLoading(false);
      setCanvasReady(true);
    }).catch(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  useEffect(() => { if (canvasReady && view === 'live') redrawAll(); }, [canvasReady, view, redrawAll]);

  // ── Live polling (strokes + text items) ─────────────────────────────────
  useEffect(() => {
    if (!canvasReady || view !== 'live') return;
    const poll = async () => {
      try {
        const res = await API.get(`/drawing/updates?strokeId=${lastStrokeIdRef.current}&textId=${lastTextIdRef.current}`);
        const newStrokes = res.data.strokes || [];
        const newTexts = res.data.textItems || [];

        if (newStrokes.length > 0) {
          const canvas = canvasRef.current;
          const ctx = canvas?.getContext('2d');
          newStrokes.forEach(s => {
            strokesRef.current.push(s);
            if (ctx) drawStroke(ctx, s, canvas.width, canvas.height);
          });
          lastStrokeIdRef.current = newStrokes[newStrokes.length - 1].id;
        }
        if (newTexts.length > 0) {
          newTexts.forEach(t => textItemsRef.current.push(t));
          lastTextIdRef.current = newTexts[newTexts.length - 1].id;
          redrawAll(); // text requires full redraw to composite correctly
        }
        if (newStrokes.length > 0 || newTexts.length > 0) {
          API.get('/drawing/canvas').then(r => setActiveUsers(r.data.activeUsers || [])).catch(() => {});
        }
      } catch { /* silent */ }
    };
    pollTimerRef.current = setInterval(poll, POLL_INTERVAL);
    return () => clearInterval(pollTimerRef.current);
  }, [canvasReady, view, drawStroke, redrawAll]);

  // ── Canvas resize ─────────────────────────────────────────────────────────
  // Use the CANVAS_W/H constants directly — the wrap is always that fixed size,
  // and offsetWidth can be stale (0 or 300 default) on first paint, causing text
  // drawn at e.g. 28 canvas-px to render 3× larger than the HTML preview.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const sync = () => {
      if (view === 'archive-detail') {
        canvas.width = CANVAS_W; canvas.height = CANVAS_H;
        if (archiveDetail) redrawCanvas(archiveDetail.strokes, archiveDetail.textItems);
      } else if (canvas.width !== CANVAS_W || canvas.height !== CANVAS_H) {
        canvas.width = CANVAS_W; canvas.height = CANVAS_H;
        if (view === 'live') redrawAll();
      }
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(canvas);
    return () => ro.disconnect();
  }, [view, archiveDetail, redrawAll, redrawCanvas, canvasReady]);

  // ── Global drag / rotate listeners for text placement ────────────────────
  useEffect(() => {
    if (!textEditActive) return;

    const onMove = (e) => {
      if (e.cancelable) e.preventDefault();
      const src = e.touches ? e.touches[0] : e;
      if (isDraggingTextRef.current && dragStartRef.current) {
        const { mx, my, ox, oy } = dragStartRef.current;
        const visualRect = wrapRef.current?.getBoundingClientRect() ?? { width: CANVAS_W, height: CANVAS_H };
        const dx = (src.clientX - mx) / visualRect.width;
        const dy = (src.clientY - my) / visualRect.height;
        setDraftText(prev => prev ? {
          ...prev,
          x: Math.min(Math.max(ox + dx, 0.02), 0.98),
          y: Math.min(Math.max(oy + dy, 0.02), 0.98),
        } : null);
      } else if (isRotatingTextRef.current && rotateStartRef.current) {
        const { cx, cy, startAngle, origRotation } = rotateStartRef.current;
        const angle = Math.atan2(src.clientY - cy, src.clientX - cx) * (180 / Math.PI);
        setDraftText(prev => prev ? { ...prev, rotation: origRotation + angle - startAngle } : null);
      }
    };

    const onUp = () => {
      isDraggingTextRef.current = false;
      isRotatingTextRef.current = false;
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onUp);
    };
  }, [textEditActive]);

  // ── Escape key exits text mode ───────────────────────────────────────────
  useEffect(() => {
    if (!isTextMode) return;
    const onKey = (e) => {
      if (e.key === 'Escape') exitTextMode();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isTextMode]);

  // Teaser countdown for upcoming non-Normal mode
  useEffect(() => {
    if (nextWeekMode === 'normal' || nextWeekMode === mode) return;
    const tick = () => {
      const now = new Date();
      const day = now.getDay();
      const diff = day === 0 ? 1 : 8 - day;
      const nextMon = new Date(now);
      nextMon.setDate(now.getDate() + diff);
      nextMon.setHours(0, 0, 0, 0);
      const ms = nextMon - now;
      const d = Math.floor(ms / 86400000);
      const h = Math.floor((ms % 86400000) / 3600000);
      const m = Math.floor((ms % 3600000) / 60000);
      setCountdown(`${d}d ${h}h ${m}m`);
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, [nextWeekMode, mode]);

  // Battle Royale live countdown
  useEffect(() => {
    if (mode !== 'battle_royale') return;
    const tick = () => {
      const now = new Date();
      const day = now.getDay();
      const diff = day === 0 ? 1 : 8 - day;
      const nextMon = new Date(now);
      nextMon.setDate(now.getDate() + diff);
      nextMon.setHours(0, 0, 0, 0);
      const ms = nextMon - now;
      const d = Math.floor(ms / 86400000);
      const h = Math.floor((ms % 86400000) / 3600000);
      const m = Math.floor((ms % 3600000) / 60000);
      setBrCountdown(`${d}d ${h}h ${m}m remaining`);
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, [mode]);

  // ── Archives ─────────────────────────────────────────────────────────────
  const loadArchives = useCallback(async () => {
    setArchivesLoading(true);
    try {
      const res = await API.get('/drawing/archives');
      setArchives(res.data.archives || []);
    } catch { setArchives([]); }
    finally { setArchivesLoading(false); }
  }, []);

  const enterArchives = useCallback(() => {
    setIsTextMode(false);
    setDraftText(null);
    setTextEditActive(false);
    setMobileDrawMode(false);
    setView('archives');
    setArchiveDetail(null);
    loadArchives();
  }, [loadArchives]);

  const enterArchiveDetail = useCallback(async (ws) => {
    setView('archive-detail');
    setArchiveDetailLoading(true);
    try {
      setMobileDrawMode(false);
      const res = await API.get(`/drawing/archive/${ws}`);
      setArchiveDetail(res.data);
    } catch { setView('archives'); }
    finally { setArchiveDetailLoading(false); }
  }, []);

  const backToLive = useCallback(() => {
    setView('live');
    setArchiveDetail(null);
    requestAnimationFrame(redrawAll);
  }, [redrawAll]);

  const backToArchives = useCallback(() => {
    setView('archives');
    setArchiveDetail(null);
    loadArchives();
  }, [loadArchives]);

  const dismissSplash = useCallback(() => {
    const key = `canvas_mode_seen_${weekStart}`;
    localStorage.setItem(key, '1');
    setShowSplash(false);
  }, [weekStart]);

  const claimColors = async () => {
    if (grabPick.length !== 2) return;
    setGrabLoading(true);
    setGrabError('');
    try {
      const res = await API.post('/drawing/claim', { color1: grabPick[0], color2: grabPick[1] });
      setClaimedColors(res.data);
      setSelectedColor(res.data.color1);
      setGrabPick([]);
    } catch (err) {
      setGrabError(err.response?.data?.message || 'Failed to claim. Try again.');
      const fresh = await API.get('/drawing/canvas');
      setAllClaimedColors(fresh.data.allClaimedColors || []);
    } finally {
      setGrabLoading(false);
    }
  };

  const toggleGrabPick = (color) => {
    setGrabPick(prev => {
      if (prev.includes(color)) return prev.filter(c => c !== color);
      if (prev.length >= 2) return prev;
      return [...prev, color];
    });
  };

  // ── Pan / zoom helpers ────────────────────────────────────────────────────
  function applyTransform(scale, tx, ty) {
    if (!Number.isFinite(scale) || !Number.isFinite(tx) || !Number.isFinite(ty)) return;
    transformRef.current = { scale, tx, ty };
    if (wrapRef.current) {
      wrapRef.current.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`;
    }
  }

  function clampTx(tx, scale) {
    return Math.min(Math.max(tx, -(CANVAS_W * scale - 80)), containerWidth - 80);
  }

  function clampTy(ty, scale) {
    return Math.min(Math.max(ty, -(CANVAS_H * scale - 80)), 80);
  }

  // ── Text mode helpers ─────────────────────────────────────────────────────
  function exitTextMode() {
    setIsTextMode(false);
    setDraftText(null);
    setTextEditActive(false);
    isDraggingTextRef.current = false;
    isRotatingTextRef.current = false;
  }

  function exitDrawMode() {
    applyTransform(1, 0, 0);
    gestureRef.current = null;
    isDrawingRef.current = false;
    activeStrokeRef.current = [];
    setMobileDrawMode(false);
    exitTextMode();
  }

  function startTextDrag(e) {
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    isDraggingTextRef.current = true;
    const src = e.touches ? e.touches[0] : e;
    const dt = draftTextRef.current;
    if (dt) dragStartRef.current = { mx: src.clientX, my: src.clientY, ox: dt.x, oy: dt.y };
  }

  function startTextRotate(e) {
    e.preventDefault();
    e.stopPropagation();
    isRotatingTextRef.current = true;
    const src = e.touches ? e.touches[0] : e;
    const dt = draftTextRef.current;
    const wrap = wrapRef.current;
    if (!dt || !wrap) return;
    const rect = wrap.getBoundingClientRect();
    const cx = rect.left + dt.x * rect.width;
    const cy = rect.top + dt.y * rect.height;
    const startAngle = Math.atan2(src.clientY - cy, src.clientX - cx) * (180 / Math.PI);
    rotateStartRef.current = { cx, cy, startAngle, origRotation: dt.rotation };
  }

  async function handleConfirmText() {
    const dt = draftTextRef.current;
    if (!dt || !dt.text.trim()) return;
    if (undoStack.length > 0) setUndoStack([]);
    try {
      const res = await API.post('/drawing/text', {
        text: dt.text,
        x: dt.x,
        y: dt.y,
        fontSize: dt.fontSize,
        color: dt.color,
        rotation: dt.rotation,
      });
      const saved = res.data.textItem;
      textItemsRef.current.push(saved);
      lastTextIdRef.current = saved.id;
      mySessionTextRef.current.push(saved);
      setMyActionCount(c => c + 1);
    } catch { /* silent — text placed locally even if save fails */ }
    exitTextMode();
    redrawAll();
  }

  // ── Drawing handlers ─────────────────────────────────────────────────────
  function getPoint(e) {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return { x: (src.clientX - rect.left) / rect.width, y: (src.clientY - rect.top) / rect.height };
  }

  async function onPointerDown(e) {
    if (view !== 'live') return;

    // Anarchy erase mode: tap a stroke to delete it
    if (mode === 'anarchy' && anarchyEraseMode) {
      const rect = canvasRef.current.getBoundingClientRect();
      const src = e.touches ? e.touches[0] : e;
      const x = (src.clientX - rect.left) / rect.width;
      const y = (src.clientY - rect.top) / rect.height;

      let closest = null;
      let minDist = Infinity;
      strokesRef.current.forEach(stroke => {
        const pts = typeof stroke.points === 'string' ? JSON.parse(stroke.points) : stroke.points;
        if (!pts) return;
        pts.forEach(pt => {
          const d = Math.hypot(pt.x - x, pt.y - y);
          if (d < minDist) { minDist = d; closest = stroke; }
        });
      });

      if (closest && minDist < 0.05) {
        try {
          await API.delete(`/drawing/stroke/${closest.id}`);
          strokesRef.current = strokesRef.current.filter(s => s.id !== closest.id);
          redrawAll();
        } catch (err) {
          console.error('Anarchy erase error', err);
        }
      }
      return; // Don't start drawing
    }

    // Two-finger gesture: start pan/zoom (mobile draw mode only)
    if (e.touches && e.touches.length >= 2 && isMobile && mobileDrawMode) {
      isDrawingRef.current = false;
      activeStrokeRef.current = [];
      const t1 = e.touches[0], t2 = e.touches[1];
      const startDist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
      if (startDist === 0) return;
      gestureRef.current = {
        startDist,
        startMidX: (t1.clientX + t2.clientX) / 2,
        startMidY: (t1.clientY + t2.clientY) / 2,
        startScale: transformRef.current.scale,
        startTx: transformRef.current.tx,
        startTy: transformRef.current.ty,
      };
      return;
    }

    if (eraseMode) {
      e.preventDefault();
      isDrawingRef.current = true;
      erasingIdsRef.current = new Set();
      return;
    }

    if (isTextMode) {
      if (!draftText) {
        e.preventDefault();
        const pt = getPoint(e);
        const dt = { x: pt.x, y: pt.y, text: '', color: selectedColor, fontSize: 28, rotation: 0 };
        setDraftText(dt);
        setTextEditActive(true);
      }
      return;
    }
    e.preventDefault();
    isDrawingRef.current = true;
    activeStrokeRef.current = [getPoint(e)];
  }

  function onPointerMove(e) {
    if (view !== 'live') return;

    // Two-finger gesture update
    if (e.touches && e.touches.length >= 2 && gestureRef.current && isMobile && mobileDrawMode) {
      if (e.cancelable) e.preventDefault();
      const t1 = e.touches[0], t2 = e.touches[1];
      const dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
      const midX = (t1.clientX + t2.clientX) / 2;
      const midY = (t1.clientY + t2.clientY) / 2;
      const { startDist, startMidX, startMidY, startScale, startTx, startTy } = gestureRef.current;
      const canvasPtX = (startMidX - startTx) / startScale;
      const canvasPtY = (startMidY - startTy) / startScale;
      const newScale = Math.min(Math.max(startScale * dist / startDist, MIN_SCALE), MAX_SCALE);
      const newTx = clampTx(midX - canvasPtX * newScale, newScale);
      const newTy = clampTy(midY - canvasPtY * newScale, newScale);
      applyTransform(newScale, newTx, newTy);
      return;
    }

    if (eraseMode && isDrawingRef.current) {
      e.preventDefault();
      const pt = getPoint(e);
      const HIT = 0.03;
      const toDelete = strokesRef.current.filter(s => {
        if (erasingIdsRef.current.has(s.id)) return false;
        if (myUserIdRef.current && s.user_id !== myUserIdRef.current) return false;
        const pts = typeof s.points === 'string' ? JSON.parse(s.points) : s.points;
        return pts && pts.some(p => Math.hypot(p.x - pt.x, p.y - pt.y) < HIT);
      });
      if (toDelete.length > 0) {
        toDelete.forEach(s => {
          erasingIdsRef.current.add(s.id);
          API.delete(`/drawing/stroke/${s.id}`).catch(() => {});
        });
        strokesRef.current = strokesRef.current.filter(s => !toDelete.some(d => d.id === s.id));
        mySessionStrokesRef.current = mySessionStrokesRef.current.filter(s => !toDelete.some(d => d.id === s.id));
        redrawAll();
      }
      return;
    }

    if (isTextMode || !isDrawingRef.current) return;
    e.preventDefault();
    const pt = getPoint(e);
    activeStrokeRef.current.push(pt);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pts = activeStrokeRef.current;
    if (pts.length >= 2) {
      const prev = pts[pts.length - 2];
      const strokeColor = mode === 'anarchy' ? anarchyColor : selectedColor;
      ctx.save();
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = brushSize;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(prev.x * canvas.width, prev.y * canvas.height);
      ctx.lineTo(pt.x * canvas.width, pt.y * canvas.height);
      ctx.stroke();
      ctx.restore();
    }
  }

  async function onPointerUp(e) {
    if (view !== 'live') return;
    if (gestureRef.current) {
      if (!e.touches || e.touches.length < 2) gestureRef.current = null;
      return;
    }
    if (eraseMode) {
      isDrawingRef.current = false;
      return;
    }
    if (isTextMode || !isDrawingRef.current) return;
    isDrawingRef.current = false;
    const pts = activeStrokeRef.current;
    activeStrokeRef.current = [];
    if (pts.length < 2) return;
    if (mode === 'storyboard' && activeDay === null) {
      return; // Storyboard complete for the week — no drawing
    }
    if (undoStack.length > 0) setUndoStack([]);
    const strokeColor = mode === 'anarchy' ? anarchyColor : selectedColor;
    try {
      const res = await API.post('/drawing/stroke', { color: strokeColor, points: pts, width: brushSize });
      const saved = res.data.stroke;
      strokesRef.current.push(saved);
      lastStrokeIdRef.current = saved.id;
      mySessionStrokesRef.current.push(saved);
      setMyActionCount(c => c + 1);
    } catch { redrawAll(); }
  }

  function onPointerLeave(e) {
    if (!isTextMode && isDrawingRef.current) onPointerUp(e);
  }

  // ── Undo / Redo ───────────────────────────────────────────────────────────
  async function handleUndo() {
    const lastStroke = mySessionStrokesRef.current[mySessionStrokesRef.current.length - 1];
    const lastText = mySessionTextRef.current[mySessionTextRef.current.length - 1];
    if (!lastStroke && !lastText) return;

    const useText = lastText && (!lastStroke || lastText.id > lastStroke.id);

    if (useText) {
      try { await API.delete(`/drawing/text/${lastText.id}`); } catch { /* already gone */ }
      mySessionTextRef.current.pop();
      textItemsRef.current = textItemsRef.current.filter(t => t.id !== lastText.id);
      setMyActionCount(c => c - 1);
      setUndoStack(prev => [...prev, { ...lastText, _undoType: 'text' }]);
    } else {
      try { await API.delete(`/drawing/stroke/${lastStroke.id}`); } catch { /* already gone */ }
      mySessionStrokesRef.current.pop();
      strokesRef.current = strokesRef.current.filter(s => s.id !== lastStroke.id);
      setMyActionCount(c => c - 1);
      setUndoStack(prev => [...prev, { ...lastStroke, _undoType: 'stroke' }]);
    }
    redrawAll();
  }

  async function handleRedo() {
    if (undoStack.length === 0) return;
    const item = undoStack[undoStack.length - 1];

    if (item._undoType === 'text') {
      try {
        const res = await API.post('/drawing/text', {
          text: item.text_content,
          x: item.x, y: item.y,
          fontSize: item.font_size,
          color: item.color,
          rotation: item.rotation,
        });
        const saved = res.data.textItem;
        textItemsRef.current.push(saved);
        lastTextIdRef.current = saved.id;
        mySessionTextRef.current.push(saved);
        setMyActionCount(c => c + 1);
        setUndoStack(prev => prev.slice(0, -1));
        redrawAll();
      } catch { /* silent */ }
    } else {
      try {
        const res = await API.post('/drawing/stroke', {
          color: item.color,
          points: item.points,
          width: item.stroke_width,
        });
        const saved = res.data.stroke;
        strokesRef.current.push(saved);
        lastStrokeIdRef.current = saved.id;
        mySessionStrokesRef.current.push(saved);
        setMyActionCount(c => c + 1);
        setUndoStack(prev => prev.slice(0, -1));
        redrawAll();
      } catch { /* silent */ }
    }
  }

  // ── Prompt suggestion ─────────────────────────────────────────────────────
  async function submitPrompt(e) {
    e.preventDefault();
    if (!promptInput.trim()) return;
    setPromptStatus('submitting');
    try {
      const res = await API.post('/drawing/prompt', { promptText: promptInput.trim() });
      setPoolCount(res.data.poolCount);
      setPromptInput('');
      setPromptStatus('success');
      setTimeout(() => setPromptStatus(null), 4000);
    } catch {
      setPromptStatus('error');
      setTimeout(() => setPromptStatus(null), 3000);
    }
  }

  // ── Storyboard suggestion ───────────────────────────────────────────────────
  const submitSuggestion = async () => {
    if (!suggestText.trim()) return;
    setSuggestMsg('');
    try {
      await API.post('/drawing/storyboard-suggest', {
        weekStart: nextWeekStart,
        canvasDay: suggestDay,
        promptText: suggestText,
      });
      setSuggestMsg('Suggestion submitted!');
      setSuggestText('');
      setTimeout(() => { setShowSuggest(false); setSuggestMsg(''); }, 1500);
    } catch (err) {
      setSuggestMsg(err.response?.data?.message || 'Failed to submit.');
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const canUndo = myActionCount > 0;
  const canRedo = undoStack.length > 0;
  const mobileScale = containerWidth > 0 ? Math.min(1, containerWidth / CANVAS_W) : 1;
  const canDraw = !isMobile || mobileDrawMode;
  const isLive = view === 'live';
  const isArchives = view === 'archives';
  const isArchiveDetail = view === 'archive-detail';

  const currentPrompt = isArchiveDetail ? archiveDetail?.prompt : prompt;
  const currentWeekLabel = isArchiveDetail
    ? `Archive — ${fmtWeek(archiveDetail?.weekStart)}`
    : weekStart ? `Week of ${fmtWeek(weekStart)}` : '';

  const canvasCursor = isLive
    ? (eraseMode ? 'cell' : isTextMode ? (draftText ? 'default' : 'text') : 'crosshair')
    : 'default';

  return (
    <div
      ref={cardRef}
      style={isMobile && mobileDrawMode ? {
        ...S.card,
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        borderRadius: 0,
        marginBottom: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
      } : S.card}
    >
      {/* ── Header ── */}
      {isMobile && mobileDrawMode ? (
        <div style={S.header}>
          <div style={S.headerLeft}>
            <span style={S.title}>Community Canvas</span>
            {weekStart && <span style={S.weekLabel}>{currentWeekLabel}</span>}
          </div>
          <div style={S.headerRight}>
            <button style={{ ...S.backBtn, minHeight: 44, minWidth: 44 }} onClick={exitDrawMode}>Done</button>
          </div>
        </div>
      ) : (
        <div style={S.header}>
          <div style={S.headerLeft}>
            <span style={S.title}>{isArchives ? 'Canvas Archives' : 'Community Canvas'}</span>
            {!isArchives && <span style={S.weekLabel}>{currentWeekLabel}</span>}
          </div>
          <div style={S.headerRight}>
            {isLive && <button style={S.archiveBtn} onClick={enterArchives}>Archives</button>}
            {isArchives && <button style={S.backBtn} onClick={backToLive}>← Current Canvas</button>}
            {isArchiveDetail && (
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={S.backBtn} onClick={backToArchives}>← Archives</button>
                <button style={S.backBtn} onClick={backToLive}>Live Canvas</button>
              </div>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <div style={S.loading}>Loading canvas…</div>
      ) : (
        <>
          {/* ── Archives list ── */}
          {isArchives && (
            <div style={S.archivesPane}>
              {archivesLoading ? (
                <div style={S.loading}>Loading archives…</div>
              ) : archives.length === 0 ? (
                <div style={S.emptyArchive}>
                  <div style={{ fontSize: 36, marginBottom: 10 }}>🎨</div>
                  <div style={{ fontWeight: 600, color: '#374151', marginBottom: 6 }}>No archives yet</div>
                  <div style={{ color: '#9CA3AF', fontSize: 13 }}>Completed weeks will appear here every Monday.</div>
                </div>
              ) : (
                <div style={S.archiveGrid}>
                  {archives.map(a => (
                    <button key={a.canvas_week} style={S.archiveCard} onClick={() => enterArchiveDetail(a.canvas_week)}>
                      <div style={S.archiveCardDate}>{fmtWeek(a.canvas_week)}</div>
                      {a.prompt_text && <div style={S.archiveCardPrompt}>"{a.prompt_text}"</div>}
                      <div style={S.archiveCardMeta}>
                        {a.stroke_count} stroke{a.stroke_count !== 1 ? 's' : ''} · {a.artist_count} artist{a.artist_count !== 1 ? 's' : ''}
                      </div>
                      <div style={S.viewLink}>View →</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Anarchy toolbar (live mode, anarchy week only) ── */}
          {isLive && mode === 'anarchy' && (!isMobile || mobileDrawMode) && (
            <div style={{ background:'rgba(220,38,38,0.08)', border:'1px solid #fca5a5', borderRadius:8, padding:'8px 10px', margin:'8px 16px 0' }}>
              <div style={{ fontSize:11, fontWeight:700, color:'#dc2626', marginBottom:6 }}>🔥 ANARCHY — Pick any color</div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:4, marginBottom:6 }}>
                {CRAYON_PALETTE.map(color => (
                  <div key={color}
                    onClick={() => setAnarchyColor(color)}
                    style={{
                      width:20, height:20, borderRadius:4,
                      background: color,
                      border: anarchyColor === color ? '2px solid #1d4ed8' : '1px solid transparent',
                      boxShadow: anarchyColor === color ? '0 0 0 2px #93c5fd' : 'none',
                      cursor:'pointer',
                    }}
                  />
                ))}
              </div>
              <button onClick={() => setAnarchyEraseMode(e => !e)}
                style={{ padding:'4px 12px', fontSize:11, background: anarchyEraseMode ? '#dc2626' : '#fff', color: anarchyEraseMode ? '#fff' : '#dc2626', border:'1px solid #dc2626', borderRadius:6, cursor:'pointer', fontWeight:600 }}>
                {anarchyEraseMode ? '✕ Erase Mode ON' : 'Erase Mode'}
              </button>
              <div style={{ fontSize:11, color:'#dc2626', marginTop:6, opacity:0.8 }}>In erase mode, tap a stroke to delete it.</div>
            </div>
          )}

          {/* ── Toolbar (live mode only) ── */}
          {isLive && (!isMobile || mobileDrawMode) && (
            <div style={S.toolbar}>
              <div style={S.toolGroup}>
                <span style={S.toolLabel}>Colors</span>
                {fullPalette && mode === 'normal'
                  ? <>
                      <button
                        onClick={() => setShowVipPicker(v => !v)}
                        style={{ ...S.colorBtn, background: selectedColor, outline: '3px solid #1D2C5F', outlineOffset: 2, position: 'relative' }}
                        title="Choose color"
                      />
                      <span style={{ fontSize: 11, color: '#6B7280', cursor: 'pointer' }} onClick={() => setShowVipPicker(v => !v)}>▼</span>
                    </>
                  : ((mode === 'battle_royale' && claimedColors)
                      ? [claimedColors.color1, claimedColors.color2]
                      : [userColors.color1, userColors.color2]
                    ).map((c, i) => (
                      <button key={i}
                        style={{ ...S.colorBtn, background: c, outline: selectedColor === c ? '3px solid #1D2C5F' : 'none', outlineOffset: 2 }}
                        onClick={() => {
                          setSelectedColor(c);
                          if (draftText) setDraftText(prev => prev ? { ...prev, color: c } : null);
                        }}
                        title={`Color ${i + 1}`}
                      />
                    ))
                }
              </div>

              {/* Brush — hidden in text mode */}
              {!isTextMode && (
                <div style={S.toolGroup}>
                  <span style={S.toolLabel}>Brush</span>
                  {BRUSH_SIZES.map(bs => (
                    <button
                      key={bs.value}
                      onClick={() => setBrushSize(bs.value)}
                      style={{ ...S.brushBtn, background: brushSize === bs.value ? '#1D2C5F' : '#F3F4F6' }}
                    >
                      <span style={{ display: 'inline-block', width: bs.dot, height: bs.dot, borderRadius: '50%', background: brushSize === bs.value ? '#fff' : '#6B7280' }} />
                    </button>
                  ))}
                </div>
              )}

              {/* Text mode toggle */}
              <div style={S.toolGroup}>
                <button
                  style={{
                    ...S.textModeBtn,
                    background: isTextMode ? '#1D2C5F' : '#F3F4F6',
                    color: isTextMode ? '#fff' : '#374151',
                    boxShadow: isTextMode ? 'inset 0 1px 3px rgba(0,0,0,0.2)' : 'none',
                  }}
                  onClick={() => { setEraseMode(false); isTextMode ? exitTextMode() : setIsTextMode(true); }}
                  title="Add text to canvas"
                >
                  <span style={{ fontFamily: 'Georgia, serif', fontWeight: 700, fontSize: 15 }}>T</span>
                </button>
              </div>

              {/* Eraser — hidden in text mode */}
              {!isTextMode && (
                <div style={S.toolGroup}>
                  <button
                    style={{
                      ...S.textModeBtn,
                      background: eraseMode ? '#DC2626' : '#F3F4F6',
                      color: eraseMode ? '#fff' : '#374151',
                      boxShadow: eraseMode ? 'inset 0 1px 3px rgba(0,0,0,0.2)' : 'none',
                    }}
                    onClick={() => setEraseMode(v => !v)}
                    title="Erase your strokes"
                  >
                    🧹
                  </button>
                </div>
              )}

              {/* Text size — shown in text mode */}
              {isTextMode && draftText && (
                <div style={S.toolGroup}>
                  <span style={S.toolLabel}>Size</span>
                  {TEXT_SIZES.map(ts => (
                    <button
                      key={ts.value}
                      style={{
                        ...S.brushBtn,
                        background: draftText.fontSize === ts.value ? '#1D2C5F' : '#F3F4F6',
                        color: draftText.fontSize === ts.value ? '#fff' : '#374151',
                        fontSize: 11, fontWeight: 700, width: 28, height: 28,
                      }}
                      onClick={() => setDraftText(prev => prev ? { ...prev, fontSize: ts.value } : null)}
                    >
                      {ts.label}
                    </button>
                  ))}
                </div>
              )}

              {/* Undo / Redo */}
              <div style={S.toolGroup}>
                <button style={{ ...S.undoRedoBtn, opacity: canUndo ? 1 : 0.35 }} disabled={!canUndo} onClick={handleUndo} title="Undo">
                  ↩ Undo
                </button>
                <button style={{ ...S.undoRedoBtn, opacity: canRedo ? 1 : 0.35 }} disabled={!canRedo} onClick={handleRedo} title="Redo">
                  ↪ Redo
                </button>
              </div>

              <div style={{ ...S.toolGroup, marginLeft: 'auto' }}>
                <span style={S.toolLabel}>Using</span>
                <span style={{ ...S.colorBtn, background: selectedColor, cursor: 'default', display: 'inline-block' }} />
              </div>
            </div>
          )}

          {/* ── Text mode hint / status bar ── */}
          {isLive && isTextMode && !draftText && (
            <div style={S.textHintBar}>
              <span style={{ fontSize: 13, color: '#374151' }}>
                Click anywhere on the canvas to place your text
              </span>
              <button style={S.textCancelBtn} onClick={exitTextMode}>✕ Cancel</button>
            </div>
          )}

          {/* ── Prompt banner ── */}
          {(isLive || isArchiveDetail) && currentPrompt && (
            <div style={S.promptBanner}>
              <span style={S.promptIcon}>✏️</span>
              <span style={S.promptText}>
                <strong>{isArchiveDetail ? "That week's prompt:" : "This week's prompt:"}</strong>{' '}{currentPrompt}
              </span>
            </div>
          )}

          {/* ── Storyboard daily panel header ── */}
          {isLive && mode === 'storyboard' && storyboard && activeDay !== null && (
            <div style={{ background:'#0f172a', color:'#fff', borderRadius:'8px 8px 0 0', padding:'10px 14px', marginBottom:0 }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
                <div>
                  <span style={{ fontWeight:700, fontSize:13 }}>📖 Chapter {activeDay + 1}: {['The World','The Character','The Tension','The Resolution'][activeDay]}</span>
                  <button onClick={() => setShowRules(true)} style={{ marginLeft:10, background:'rgba(255,255,255,0.15)', border:'none', color:'#fff', borderRadius:10, padding:'2px 8px', fontSize:11, cursor:'pointer' }}>
                    📖 Story so far
                  </button>
                </div>
              </div>
              {dailyPrompt && <div style={{ fontSize:12, opacity:0.8, marginBottom:8, fontStyle:'italic' }}>"{dailyPrompt}"</div>}
              <div style={{ display:'flex', gap:4 }}>
                {[0,1,2,3].map(d => (
                  <div key={d} style={{ flex:1 }}>
                    <div style={{ height:4, borderRadius:2, background: d < activeDay ? 'rgba(255,255,255,0.9)' : d === activeDay ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.15)' }} />
                    <div style={{ fontSize:9, color:'rgba(255,255,255,0.6)', textAlign:'center', marginTop:2 }}>
                      {['Mon','Tue','Wed','Thu'][d]}{d < activeDay ? ' ✓' : d === activeDay ? ' ←' : ''}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Storyboard 4-panel reveal (Fri–Sun: mode=storyboard, activeDay=null) ── */}
          {isLive && mode === 'storyboard' && activeDay === null && storyboard && (
            <div style={{ padding:16 }}>
              <div style={{ background:'#0f172a', color:'#fff', borderRadius:8, padding:'12px 16px', textAlign:'center', marginBottom:16 }}>
                <div style={{ fontSize:22 }}>📖</div>
                <div style={{ fontWeight:800, fontSize:16 }}>This Week's Story — Complete</div>
                <div style={{ fontSize:12, opacity:0.7, marginTop:2 }}>Four chapters. One collective story.</div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                {storyboard.panels.map(panel => (
                  <div key={panel.day} style={{ border:'2px solid #0f172a', borderRadius:8, overflow:'hidden' }}>
                    <div style={{ background:'#0f172a', color:'#fff', fontSize:11, fontWeight:700, padding:'4px 10px' }}>
                      Chapter {panel.day + 1} — {panel.chapterName}
                    </div>
                    {panel.prompt && (
                      <div style={{ fontSize:11, color:'#475569', padding:'4px 10px', borderBottom:'1px solid #e2e8f0', fontStyle:'italic' }}>
                        "{panel.prompt}"
                      </div>
                    )}
                    <StoryboardPanel strokes={panel.strokes} textItems={panel.textItems} />
                  </div>
                ))}
              </div>
              <div style={{ marginTop:12, textAlign:'center', fontSize:12, color:'#94a3b8' }}>
                Read-only — this story is complete. Next week begins Monday.
              </div>
            </div>
          )}

          {/* ── Storyboard fallback (Fri–Sun, no storyboard data) ── */}
          {isLive && mode === 'storyboard' && activeDay === null && !storyboard && (
            <div style={{ padding:32, textAlign:'center', color:'#64748b' }}>
              <div style={{ fontSize:32, marginBottom:8 }}>📖</div>
              <div style={{ fontWeight:700, fontSize:15, marginBottom:4 }}>Storyboard Week is complete</div>
              <div style={{ fontSize:13 }}>The canvas rests until Monday.</div>
            </div>
          )}

          {/* ── Battle Royale / teaser banners (outside the framed canvas) ── */}
          {isLive && nextWeekMode !== 'normal' && nextWeekMode !== mode && MODE_RULES[nextWeekMode] && (
            <div style={{ background:'#1e293b', color:'#fff', borderRadius:8, padding:'8px 14px', margin:'0 16px 8px', display:'flex', alignItems:'center', justifyContent:'space-between', fontSize:13 }}>
              <span>{MODE_RULES[nextWeekMode].emoji} <strong>Next week: {MODE_RULES[nextWeekMode].title.replace('!','')}</strong> — starts Monday</span>
              <span style={{ fontSize:12, opacity:0.7, fontFamily:'monospace' }}>{countdown}</span>
            </div>
          )}
          {isLive && mode === 'battle_royale' && claimedColors && (
            <div style={{ background:'linear-gradient(135deg,#7c3aed,#db2777)', color:'#fff', borderRadius:8, padding:'6px 14px', margin:'0 16px 8px', display:'flex', alignItems:'center', justifyContent:'space-between', fontSize:13 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ fontWeight:700 }}>⚔️ Battle Royale</span>
                <div style={{ width:14, height:14, borderRadius:'50%', background:claimedColors.color1, border:'1px solid rgba(255,255,255,0.4)' }} />
                <div style={{ width:14, height:14, borderRadius:'50%', background:claimedColors.color2, border:'1px solid rgba(255,255,255,0.4)' }} />
              </div>
              <span style={{ fontFamily:'monospace', fontSize:12 }}>{brCountdown}</span>
            </div>
          )}
          {isLive && mode === 'battle_royale' && !claimedColors && (
            <div style={{ background:'linear-gradient(135deg,#7c3aed,#db2777)', color:'#fff', borderRadius:8, padding:'10px 14px', margin:'0 16px 8px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div>
                <div style={{ fontWeight:700, fontSize:13 }}>⚔️ Battle Royale Week — grab your colors!</div>
                <div style={{ fontSize:11, opacity:0.85 }}>Pick 2 colors before someone else claims them.</div>
              </div>
              <div style={{ fontFamily:'monospace', fontWeight:800, fontSize:16 }}>{brCountdown}</div>
            </div>
          )}

          {/* ── Canvas (live + archive-detail) ── */}
          {!isArchives && !(isLive && mode === 'storyboard' && activeDay === null && storyboard) && (
            <div style={isMobile && !mobileDrawMode
              ? { overflowX: 'hidden', height: CANVAS_H * mobileScale }
              : S.canvasScrollOuter
            }>
              <div ref={wrapRef} style={isMobile && !mobileDrawMode
                ? { ...S.canvasWrap, transform: `scale(${mobileScale})`, transformOrigin: 'top left', cursor: 'default' }
                : { ...S.canvasWrap, cursor: canvasCursor, transformOrigin: '0 0' }
              }>
                {archiveDetailLoading && (
                  <div style={{ ...S.loading, position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    Loading archive…
                  </div>
                )}

                {/* Mode chip */}
                {mode !== 'normal' && MODE_RULES[mode] && (
                  <button
                    onClick={() => setShowRules(true)}
                    style={{ position:'absolute', top:8, right:8, zIndex:20, background:'rgba(0,0,0,0.7)', color:'#fff', border:'none', borderRadius:20, padding:'4px 12px', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                    {MODE_RULES[mode].emoji} {MODE_RULES[mode].title.replace(' Week!','').replace('!','')}
                  </button>
                )}

                {/* Splash modal */}
                {showSplash && MODE_RULES[mode] && (
                  <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.6)', zIndex:50, display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <div style={{ background:'#fff', borderRadius:12, padding:24, maxWidth:360, margin:16 }}>
                      <div style={{ textAlign:'center', fontSize:32, marginBottom:8 }}>{MODE_RULES[mode].emoji}</div>
                      <div style={{ textAlign:'center', fontWeight:800, fontSize:18, color:'#1e293b', marginBottom:12 }}>{MODE_RULES[mode].title}</div>
                      <ul style={{ paddingLeft:20, margin:'0 0 16px', color:'#475569', fontSize:13 }}>
                        {MODE_RULES[mode].rules.map((r,i) => <li key={i} style={{ marginBottom:6 }}>{r}</li>)}
                      </ul>
                      <button onClick={dismissSplash} style={{ width:'100%', padding:'9px', background:'#2563eb', color:'#fff', border:'none', borderRadius:8, fontWeight:700, fontSize:14, cursor:'pointer' }}>
                        Got it — let's draw!
                      </button>
                    </div>
                  </div>
                )}

                {/* Rules modal */}
                {showRules && MODE_RULES[mode] && (
                  <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.6)', zIndex:50, display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <div style={{ background:'#fff', borderRadius:12, padding:24, maxWidth:360, margin:16 }}>
                      <div style={{ textAlign:'center', fontSize:32, marginBottom:8 }}>{MODE_RULES[mode].emoji}</div>
                      <div style={{ textAlign:'center', fontWeight:800, fontSize:18, color:'#1e293b', marginBottom:12 }}>{MODE_RULES[mode].title}</div>
                      <ul style={{ paddingLeft:20, margin:'0 0 16px', color:'#475569', fontSize:13 }}>
                        {MODE_RULES[mode].rules.map((r,i) => <li key={i} style={{ marginBottom:6 }}>{r}</li>)}
                      </ul>
                      <button onClick={() => setShowRules(false)} style={{ width:'100%', padding:'9px', background:'#2563eb', color:'#fff', border:'none', borderRadius:8, fontWeight:700, fontSize:14, cursor:'pointer' }}>
                        Close
                      </button>
                    </div>
                  </div>
                )}

                {/* Battle Royale color grab popup */}
                {mode === 'battle_royale' && !claimedColors && (
                  <div style={{ position:'absolute', inset:0, background:'rgba(255,255,255,0.85)', zIndex:40, display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <div style={{ background:'#fff', border:'2px solid #7c3aed', borderRadius:12, padding:20, maxWidth:400, width:'90%', boxShadow:'0 4px 24px rgba(0,0,0,0.15)' }}>
                      <div style={{ textAlign:'center', marginBottom:10 }}>
                        <div style={{ fontSize:28 }}>⚔️</div>
                        <div style={{ fontWeight:800, fontSize:16, color:'#7c3aed' }}>Battle Royale Week!</div>
                        <div style={{ fontSize:12, color:'#64748b', marginTop:4 }}>
                          Pick any 2 colors. Claimed colors are gone — move fast.<br/>
                          <strong>{allClaimedColors.length}</strong> color{allClaimedColors.length !== 1 ? 's' : ''} already taken.
                        </div>
                      </div>

                      <div style={{ display:'grid', gridTemplateColumns:'repeat(8,1fr)', gap:5, marginBottom:14 }}>
                        {CRAYON_PALETTE.map(color => {
                          const taken = allClaimedColors.includes(color);
                          const picked = grabPick.includes(color);
                          return (
                            <div key={color}
                              onClick={() => !taken && toggleGrabPick(color)}
                              style={{
                                aspectRatio:'1', borderRadius:5,
                                background: taken ? '#e2e8f0' : color,
                                border: picked ? '2px solid #1d4ed8' : '1px solid transparent',
                                boxShadow: picked ? '0 0 0 2px #93c5fd' : 'none',
                                cursor: taken ? 'not-allowed' : 'pointer',
                                display:'flex', alignItems:'center', justifyContent:'center',
                                fontSize:10, color:'#fff', fontWeight:700,
                                opacity: taken ? 0.4 : 1,
                              }}>
                              {taken ? '✕' : ''}
                            </div>
                          );
                        })}
                      </div>

                      <div style={{ textAlign:'center', fontSize:12, color:'#7c3aed', fontWeight:600, marginBottom:10 }}>
                        {grabPick.length} / 2 selected
                        {grabPick.length === 2 && (
                          <span style={{ marginLeft:8 }}>
                            <span style={{ display:'inline-block', width:14, height:14, borderRadius:'50%', background:grabPick[0], border:'1px solid #e2e8f0', verticalAlign:'middle' }} />
                            {' '}
                            <span style={{ display:'inline-block', width:14, height:14, borderRadius:'50%', background:grabPick[1], border:'1px solid #e2e8f0', verticalAlign:'middle' }} />
                          </span>
                        )}
                      </div>

                      {grabError && <div style={{ background:'#fee2e2', color:'#991b1b', padding:'6px 10px', borderRadius:6, fontSize:12, marginBottom:8 }}>{grabError}</div>}

                      <button
                        onClick={claimColors}
                        disabled={grabPick.length !== 2 || grabLoading}
                        style={{ width:'100%', padding:'9px', background: grabPick.length === 2 ? '#7c3aed' : '#e2e8f0', color: grabPick.length === 2 ? '#fff' : '#94a3b8', border:'none', borderRadius:8, fontWeight:700, fontSize:14, cursor: grabPick.length === 2 ? 'pointer' : 'not-allowed' }}>
                        {grabLoading ? 'Claiming…' : 'Claim My Colors & Start Drawing'}
                      </button>
                    </div>
                  </div>
                )}

                <canvas
                  ref={canvasRef}
                  style={{ ...S.canvas, opacity: archiveDetailLoading ? 0 : 1, touchAction: canDraw ? 'none' : 'auto' }}
                  {...(canDraw ? {
                    onMouseDown: onPointerDown,
                    onMouseMove: onPointerMove,
                    onMouseUp: onPointerUp,
                    onMouseLeave: onPointerLeave,
                    onTouchStart: onPointerDown,
                    onTouchMove: onPointerMove,
                    onTouchEnd: onPointerUp,
                  } : {})}
                />
                {/* Text placement overlay */}
                {isLive && canDraw && draftText && (
                  <TextPreview
                    draft={draftText}
                    onDragStart={startTextDrag}
                    onRotateStart={startTextRotate}
                  />
                )}
              </div>
            </div>
          )}

          {/* ── Mobile: Draw button (view mode only) ── */}
          {isMobile && isLive && !isArchives && !mobileDrawMode && (
            <button style={S.mobileDrawBtn} onClick={() => setMobileDrawMode(true)}>
              ✏️  Draw
            </button>
          )}

          {/* ── Text input strip (shown while placing text) ── */}
          {isLive && isTextMode && draftText && (
            <div style={S.textInputStrip}>
              <input
                autoFocus
                type="text"
                value={draftText.text}
                onChange={e => setDraftText(prev => prev ? { ...prev, text: e.target.value } : null)}
                placeholder="Type your text…"
                maxLength={120}
                style={S.textStripInput}
                onKeyDown={e => {
                  if (e.key === 'Enter') handleConfirmText();
                  if (e.key === 'Escape') exitTextMode();
                }}
              />
              <button
                style={{ ...S.textConfirmBtn, opacity: draftText.text.trim() ? 1 : 0.4 }}
                disabled={!draftText.text.trim()}
                onClick={handleConfirmText}
                title="Place text (Enter)"
              >
                ✓
              </button>
              <button style={S.textCancelBtn} onClick={exitTextMode} title="Cancel (Escape)">✕</button>
            </div>
          )}

          {/* ── Archive artists ── */}
          {isArchiveDetail && archiveDetail?.artists?.length > 0 && (
            <div style={S.legend}>
              <span style={S.toolLabel}>Artists that week:</span>
              {archiveDetail.artists.map((a, i) => (
                <span key={i} style={S.legendUser}>
                  <span style={{ ...S.legendDot, background: a.color1 }} />
                  <span style={{ ...S.legendDot, background: a.color2, marginLeft: 2 }} />
                  <span style={S.legendName}>{a.name}</span>
                </span>
              ))}
            </div>
          )}

          {/* ── Live: active users ── */}
          {isLive && activeUsers.length > 0 && (
            <div style={S.legend}>
              <span style={S.toolLabel}>This week:</span>
              {activeUsers.map(u => (
                <span key={u.id} style={S.legendUser}>
                  <span style={{ ...S.legendDot, background: u.color1 }} />
                  <span style={{ ...S.legendDot, background: u.color2, marginLeft: 2 }} />
                  <span style={S.legendName}>{u.name}</span>
                </span>
              ))}
            </div>
          )}

          {/* ── Suggest a prompt ── */}
          {isLive && (
            <div style={S.promptSection}>
              <div style={S.promptSectionTitle}>
                <span style={{ fontSize: 15 }}>💡</span>
                <span>Suggest a Prompt for a Future Week</span>
              </div>
              <form onSubmit={submitPrompt} style={S.promptForm}>
                <input
                  type="text"
                  value={promptInput}
                  onChange={e => setPromptInput(e.target.value)}
                  placeholder="e.g. Draw your morning commute, A rainy day scene…"
                  maxLength={200}
                  style={S.promptInput}
                  disabled={promptStatus === 'submitting'}
                />
                <button
                  type="submit"
                  disabled={!promptInput.trim() || promptStatus === 'submitting'}
                  style={{ ...S.promptSubmitBtn, opacity: !promptInput.trim() || promptStatus === 'submitting' ? 0.55 : 1 }}
                >
                  {promptStatus === 'submitting' ? 'Sending…' : 'Suggest'}
                </button>
              </form>
              {promptStatus === 'success' && <div style={S.promptSuccess}>Your prompt has been added to the pool!</div>}
              {promptStatus === 'error' && <div style={S.promptError}>Something went wrong. Please try again.</div>}
              {poolCount > 0 && promptStatus !== 'success' && (
                <div style={S.poolNote}>{poolCount} prompt{poolCount !== 1 ? 's' : ''} waiting in the pool</div>
              )}
            </div>
          )}

          {/* ── VIP full-palette picker panel ── */}
          {showVipPicker && fullPalette && mode === 'normal' && (
            <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:100, display:'flex', alignItems:'center', justifyContent:'center' }}
              onClick={() => setShowVipPicker(false)}>
              <div style={{ background:'#fff', borderRadius:12, padding:20, maxWidth:340, width:'90%', boxShadow:'0 4px 24px rgba(0,0,0,0.2)' }}
                onClick={e => e.stopPropagation()}>
                <div style={{ fontWeight:700, fontSize:14, marginBottom:12, color:'#1e293b' }}>🎨 Choose a color</div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(8,1fr)', gap:8 }}>
                  {CRAYON_PALETTE.map(c => (
                    <button key={c}
                      onClick={() => {
                        setSelectedColor(c);
                        if (draftText) setDraftText(prev => prev ? { ...prev, color: c } : null);
                        setShowVipPicker(false);
                      }}
                      style={{
                        width:'100%', aspectRatio:'1', borderRadius:6, border:'none', cursor:'pointer',
                        background: c,
                        outline: selectedColor === c ? '3px solid #1D2C5F' : '2px solid transparent',
                        outlineOffset: 2,
                        boxShadow: selectedColor === c ? '0 0 0 2px #93c5fd' : 'none',
                      }}
                    />
                  ))}
                </div>
                <button onClick={() => setShowVipPicker(false)}
                  style={{ marginTop:14, width:'100%', padding:'8px', background:'#f1f5f9', border:'none', borderRadius:8, fontSize:13, cursor:'pointer' }}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* ── Storyboard chapter suggestion ── */}
          {isLive && nextWeekMode === 'storyboard' && (
            <div style={{ textAlign:'center', marginTop:8 }}>
              <button onClick={() => setShowSuggest(true)}
                style={{ fontSize:11, color:'#3b82f6', background:'none', border:'none', cursor:'pointer', textDecoration:'underline' }}>
                📖 Suggest a chapter prompt for next week
              </button>
            </div>
          )}

          {showSuggest && (
            <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:100, display:'flex', alignItems:'center', justifyContent:'center' }}>
              <div style={{ background:'#fff', borderRadius:12, padding:24, maxWidth:380, width:'90%' }}>
                <div style={{ fontWeight:700, fontSize:16, marginBottom:12 }}>📖 Suggest a Storyboard Prompt</div>
                <div style={{ marginBottom:10 }}>
                  <label style={{ fontSize:12, fontWeight:600, color:'#64748b', display:'block', marginBottom:4 }}>Which chapter?</label>
                  <select value={suggestDay} onChange={e => setSuggestDay(Number(e.target.value))}
                    style={{ width:'100%', padding:'6px 8px', border:'1px solid #cbd5e1', borderRadius:6, fontSize:13 }}>
                    <option value={0}>Monday — Chapter 1: The World</option>
                    <option value={1}>Tuesday — Chapter 2: The Character</option>
                    <option value={2}>Wednesday — Chapter 3: The Tension</option>
                    <option value={3}>Thursday — Chapter 4: The Resolution</option>
                  </select>
                </div>
                <div style={{ marginBottom:12 }}>
                  <label style={{ fontSize:12, fontWeight:600, color:'#64748b', display:'block', marginBottom:4 }}>Your prompt idea</label>
                  <textarea value={suggestText} onChange={e => setSuggestText(e.target.value)}
                    rows={3} maxLength={200}
                    placeholder="Describe what to draw for this chapter…"
                    style={{ width:'100%', padding:'6px 8px', border:'1px solid #cbd5e1', borderRadius:6, fontSize:13, boxSizing:'border-box', resize:'vertical' }} />
                </div>
                {suggestMsg && <div style={{ fontSize:13, color: suggestMsg.includes('!') ? '#166534' : '#991b1b', marginBottom:8 }}>{suggestMsg}</div>}
                <div style={{ display:'flex', gap:8 }}>
                  <button onClick={submitSuggestion}
                    style={{ flex:1, padding:'8px', background:'#2563eb', color:'#fff', border:'none', borderRadius:8, fontWeight:700, fontSize:13, cursor:'pointer' }}>
                    Submit
                  </button>
                  <button onClick={() => setShowSuggest(false)}
                    style={{ padding:'8px 16px', background:'#f1f5f9', border:'none', borderRadius:8, fontSize:13, cursor:'pointer' }}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ── Styles ─────────────────────────────────────────────────────────────── */
const S = {
  card: {
    background: '#fff',
    borderRadius: 12,
    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
    border: '1px solid #E5E7EB',
    overflow: 'hidden',
    marginBottom: 24,
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 20px', background: '#1D2C5F', color: '#fff',
  },
  headerLeft: { display: 'flex', flexDirection: 'column', gap: 2 },
  headerRight: { display: 'flex', alignItems: 'center', gap: 8 },
  title: { fontWeight: 700, fontSize: 15, letterSpacing: '0.02em' },
  weekLabel: { fontSize: 11, opacity: 0.75, letterSpacing: '0.04em' },
  archiveBtn: {
    background: 'rgba(255,255,255,0.15)', color: '#fff',
    border: '1px solid rgba(255,255,255,0.35)', borderRadius: 6,
    padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
  },
  backBtn: {
    background: 'rgba(255,255,255,0.15)', color: '#fff',
    border: '1px solid rgba(255,255,255,0.35)', borderRadius: 6,
    padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
  },
  loading: { padding: '2.5rem', textAlign: 'center', color: '#9CA3AF', fontSize: 14 },
  toolbar: {
    display: 'flex', alignItems: 'center', gap: 16,
    padding: '10px 16px', borderBottom: '1px solid #F3F4F6',
    flexWrap: 'wrap', background: '#FAFAF9',
  },
  toolGroup: { display: 'flex', alignItems: 'center', gap: 6 },
  toolLabel: {
    fontSize: 11, fontWeight: 600, color: '#6B7280',
    textTransform: 'uppercase', letterSpacing: '0.05em', marginRight: 2,
  },
  colorBtn: { width: 26, height: 26, borderRadius: '50%', border: 'none', cursor: 'pointer', padding: 0 },
  brushBtn: {
    width: 32, height: 32, borderRadius: 6, border: 'none', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  textModeBtn: {
    width: 32, height: 32, borderRadius: 6, border: 'none', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'background 0.15s',
  },
  // Text hint bar (shown when text mode is active but no draft yet)
  textHintBar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '8px 16px', background: '#F0FDF4', borderBottom: '1px solid #BBF7D0',
    fontSize: 13, color: '#166534',
  },
  // Text input strip (shown while editing draft text)
  textInputStrip: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '10px 16px', background: '#F8FAFC', borderBottom: '1px solid #E2E8F0',
  },
  textStripInput: {
    flex: 1, padding: '8px 12px',
    border: '1.5px solid #CBD5E1', borderRadius: 6,
    fontSize: 14, color: '#111827', background: '#fff',
    outline: 'none', minWidth: 0,
  },
  textConfirmBtn: {
    padding: '7px 14px', background: '#1D2C5F', color: '#fff',
    border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 15,
    cursor: 'pointer', flexShrink: 0,
  },
  textCancelBtn: {
    padding: '7px 12px', background: '#F1F5F9', color: '#64748B',
    border: '1px solid #CBD5E1', borderRadius: 6, fontWeight: 600, fontSize: 13,
    cursor: 'pointer', flexShrink: 0,
  },
  promptBanner: {
    display: 'flex', alignItems: 'flex-start', gap: 8,
    padding: '10px 16px', background: '#EEF2FF', borderBottom: '1px solid #E0E7FF',
    fontSize: 13, color: '#3730A3',
  },
  promptIcon: { fontSize: 15, flexShrink: 0, marginTop: 1 },
  promptText: { lineHeight: 1.45 },
  canvasScrollOuter: { overflowX: 'auto', padding: FRAME_GUTTER },
  canvasWrap: {
    position: 'relative',
    width: CANVAS_W,
    height: CANVAS_H,
    background: '#fff',
    boxShadow: [
      'inset 0 0 0 1px rgba(0,0,0,0.06)',
      `0 0 0 ${FRAME_MAT}px #F2EAD8`,
      `0 0 0 ${FRAME_MAT + FRAME_WOOD}px #3D2008`,
      `0 0 0 ${FRAME_MAT + FRAME_WOOD + 2}px #5C3415`,
      '0 8px 28px rgba(0,0,0,0.45)',
    ].join(', '),
  },
  canvas: {
    display: 'block', width: '100%', height: '100%',
    touchAction: 'none', transition: 'opacity 0.15s',
  },
  legend: {
    display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12,
    padding: '10px 16px', borderTop: '1px solid #F3F4F6', background: '#FAFAF9',
  },
  legendUser: { display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#374151' },
  legendDot: { display: 'inline-block', width: 10, height: 10, borderRadius: '50%' },
  legendName: { marginLeft: 2 },
  archivesPane: { padding: '20px' },
  emptyArchive: { textAlign: 'center', padding: '3rem 1rem', color: '#9CA3AF' },
  archiveGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 },
  archiveCard: {
    background: '#FAFAF9', border: '1px solid #E5E7EB', borderRadius: 10,
    padding: '14px 16px', textAlign: 'left', cursor: 'pointer', width: '100%',
  },
  archiveCardDate: { fontWeight: 700, color: '#1D2C5F', fontSize: 13, marginBottom: 4 },
  archiveCardPrompt: { fontSize: 12, color: '#6B7280', fontStyle: 'italic', marginBottom: 6, lineHeight: 1.4 },
  archiveCardMeta: { fontSize: 11, color: '#9CA3AF', marginBottom: 8 },
  viewLink: { fontSize: 12, color: '#C67A52', fontWeight: 600 },
  promptSection: { borderTop: '1px solid #F3F4F6', padding: '14px 16px', background: '#FAFAF9' },
  promptSectionTitle: { display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, fontSize: 13, color: '#374151', marginBottom: 10 },
  promptForm: { display: 'flex', gap: 8 },
  promptInput: {
    flex: 1, padding: '7px 11px', border: '1px solid #D1D5DB', borderRadius: 6,
    fontSize: 13, color: '#111827', background: '#fff', minWidth: 0,
  },
  promptSubmitBtn: {
    padding: '7px 16px', background: '#1D2C5F', color: '#fff',
    border: 'none', borderRadius: 6, fontWeight: 600, fontSize: 13,
    cursor: 'pointer', whiteSpace: 'nowrap',
  },
  promptSuccess: { marginTop: 8, fontSize: 12, color: '#065F46', fontWeight: 600 },
  promptError: { marginTop: 8, fontSize: 12, color: '#991B1B' },
  poolNote: { marginTop: 6, fontSize: 11, color: '#9CA3AF' },
  undoRedoBtn: {
    display: 'flex', alignItems: 'center', gap: 4,
    padding: '4px 10px', borderRadius: 6, border: '1px solid #D1D5DB',
    background: '#fff', fontSize: 12, fontWeight: 600, color: '#374151',
    cursor: 'pointer', whiteSpace: 'nowrap',
  },
  mobileDrawBtn: {
    display: 'block',
    width: '100%',
    padding: '13px',
    background: '#1D2C5F',
    color: '#fff',
    border: 'none',
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
    letterSpacing: '0.03em',
    textAlign: 'center',
  },
};
